// The pdf.js seam for the in-app PDF viewer (ADR-0027).
//
// PDFs are rendered with pdf.js onto <canvas> inside our own viewer, never in
// the browser's PDF plugin (whose toolbar offers Download and Print to a
// view-only buyer). This module is the ONLY place that touches `pdfjs-dist`:
//   - it is imported lazily (`import('pdfjs-dist')`), so pdf.js lands in its own
//     chunk and only loads when a PDF is opened;
//   - the worker and the two no-wasm image decoders are served same-origin from
//     `/pdfjs/<version>/` (emitted by `web/shared/vite/pdfjs-assets.ts`), so the
//     CSP needs no CDN, no `blob:` worker and no `'wasm-unsafe-eval'`;
//   - `useWasm: false` keeps WebAssembly out of the worker: JBIG2/CCITT (scanned
//     faxes) and JPEG 2000 images decode with pdf.js's pure-JS fallbacks.
//
// The viewer talks to the small `PdfDocumentLike` shape below rather than to
// pdf.js types, so tests can hand it a fake document.
import type { PDFPageProxy } from 'pdfjs-dist';

type PageViewport = Parameters<PDFPageProxy['render']>[0]['viewport'];

export interface PdfViewportLike {
  width: number;
  height: number;
}

export interface PdfRenderTaskLike {
  promise: Promise<unknown>;
  cancel(): void;
}

export interface PdfPageLike {
  getViewport(params: { scale: number }): PdfViewportLike;
  render(params: {
    canvas: HTMLCanvasElement;
    viewport: PdfViewportLike;
    transform?: number[];
  }): PdfRenderTaskLike;
}

export interface PdfDocumentLike {
  numPages: number;
  getPage(pageNumber: number): Promise<PdfPageLike>;
  destroy(): unknown;
}

/** Why a PDF could not be opened — drives the viewer's friendly message. */
export type PdfLoadFailure = 'encrypted' | 'invalid' | 'failed';

export class PdfLoadError extends Error {
  constructor(readonly reason: PdfLoadFailure) {
    super(`PDF could not be opened (${reason})`);
    this.name = 'PdfLoadError';
  }
}

/** Where the pdf.js worker + decoder modules are served for a given version.
 * Must match the path `pdfjsAssets()` emits. Versioned so an upgrade never
 * serves a stale worker against a new API build. */
export function pdfjsAssetBase(version: string): string {
  return `/pdfjs/${version}/`;
}

export const PDFJS_WORKER_FILE = 'pdf.worker.min.js';

/**
 * Open PDF bytes with pdf.js. The bytes are handed to the worker (pdf.js may
 * transfer the buffer), so pass a copy you no longer need.
 * Rejects with `PdfLoadError` — `encrypted` for a password-protected file,
 * `invalid` for a damaged/non-PDF file, `failed` for anything else.
 */
export async function loadPdfDocument(data: Uint8Array): Promise<PdfDocumentLike> {
  const pdfjs = await import('pdfjs-dist');
  const base = pdfjsAssetBase(pdfjs.version);
  pdfjs.GlobalWorkerOptions.workerSrc = `${base}${PDFJS_WORKER_FILE}`;

  const task = pdfjs.getDocument({
    data,
    // Decoder fallbacks (jbig2/openjpeg *_nowasm_fallback.js) load from here.
    wasmUrl: base,
    useWasm: false,
    // XFA forms render as HTML; the canvas-only viewer never shows them.
    enableXfa: false,
  });
  try {
    const pdf = await task.promise;
    return {
      numPages: pdf.numPages,
      getPage: async (pageNumber) => {
        const page = await pdf.getPage(pageNumber);
        return {
          getViewport: (params) => page.getViewport(params),
          // `viewport` is always one this page's getViewport returned.
          render: ({ canvas, viewport, transform }) =>
            page.render({ canvas, viewport: viewport as PageViewport, transform }),
        };
      },
      // Destroying the loading task tears down the document and its worker.
      destroy: () => task.destroy(),
    };
  } catch (err) {
    // Release the worker; the failed document is never handed out.
    void task.destroy();
    const name = (err as { name?: unknown } | null)?.name;
    if (name === 'PasswordException') throw new PdfLoadError('encrypted');
    if (name === 'InvalidPDFException') throw new PdfLoadError('invalid');
    throw new PdfLoadError('failed');
  }
}
